import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableRowsSkeleton } from "../skeleton/Skeleton";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectPaymentTypes,
  selectPaymentTypesStatus,
} from "../../features/payment-types/payment-types-slice";
import {
  fetchPaymentTypes,
  archivePaymentType,
} from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import { PaymentType } from "../../features/payment-types/models/PaymentType";
import { formatMoney } from "../../features/billing/utils/money";
import { exportToExcel } from "../../features/billing/utils/excelExport";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import FormModal from "./FormModal";
import PagerControls from "./PagerControls";
import PaymentTypeForm from "./PaymentTypeForm";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

const PAGE_SIZE = 20;

/** The instructor's price catalog: list, create, rename/re-price, archive. */
const BillingPaymentTypesTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const paymentTypes = useAppSelector(selectPaymentTypes);
  const status = useAppSelector(selectPaymentTypesStatus);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PaymentType | null>(null);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(paymentTypes.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleRows = paymentTypes.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reload = () => dispatch(fetchPaymentTypes({ includeArchived: true }));

  const handleExport = () => {
    exportToExcel(
      "payment-types",
      paymentTypes.map((pt) => ({
        [t("billing.table.name")]: pt.name,
        [t("billing.table.price")]: formatMoney(pt.priceAmountMinorUnits, pt.currency, getDisplayLocale()),
        [t("billing.table.status")]: pt.isActive
          ? t("billing.paymentTypes.active")
          : t("billing.paymentTypes.archived"),
      })),
    );
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleArchive = async (id: string) => {
    await dispatch(archivePaymentType(id));
    reload();
  };

  return (
    <div className="mt-4 space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)}>
          {t("billing.actions.newPaymentType")}
        </button>
        <button type="button" className="btn btn-secondary ml-auto" onClick={handleExport}>
          {t("billing.common.exportToExcel")}
        </button>
      </div>

      {(showForm || editing) && (
        <FormModal onClose={closeForm}>
          <PaymentTypeForm
            key={editing?.id ?? "new"}
            paymentType={editing ?? undefined}
            onSaved={() => { closeForm(); reload(); }}
            onClose={closeForm}
          />
        </FormModal>
      )}

      {status === LoadingStatus.Loading && paymentTypes.length === 0 ? (
        <TableRowsSkeleton rows={5} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-grey-90 dark:border-primary-20">
          <table className="w-full text-sm">
            <thead className="bg-grey-95 dark:bg-primary-20 text-grey-30 dark:text-grey-90">
              <tr>
                <th className="px-4 py-2 text-left">{t("billing.table.name")}</th>
                <th className="px-4 py-2 text-left">{t("billing.table.price")}</th>
                <th className="px-4 py-2 text-left">{t("billing.table.active")}</th>
                <th className="px-4 py-2 text-right">{t("billing.table.actions")}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-primary-10 text-grey-20 dark:text-white">
              {visibleRows.map((pt) => (
                <tr
                  key={pt.id}
                  onClick={() => pt.isActive && setEditing(pt)}
                  className={`border-t border-grey-90 dark:border-primary-20 ${
                    pt.isActive ? "cursor-pointer hover:bg-grey-95 dark:hover:bg-primary-20" : ""
                  }`}
                >
                  <td className={`px-4 py-2 ${pt.isActive ? "" : "text-grey-50 line-through dark:text-grey-60"}`}>{pt.name}</td>
                  <td className="px-4 py-2">{formatMoney(pt.priceAmountMinorUnits, pt.currency, getDisplayLocale())}</td>
                  <td className="px-4 py-2">{pt.isActive ? t("billing.paymentTypes.yes") : t("billing.paymentTypes.archived")}</td>
                  <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    {pt.isActive && (
                      <>
                        <button type="button" className="btn btn-text" onClick={() => setEditing(pt)}>
                          {t("billing.rowActions.edit")}
                        </button>
                        <button type="button" className="btn btn-text" onClick={() => handleArchive(pt.id)}>
                          {t("billing.paymentTypes.archive")}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {paymentTypes.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-grey-40 dark:text-grey-80" colSpan={4}>
                    {t("billing.paymentTypes.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <PagerControls page={currentPage} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
};

export default BillingPaymentTypesTab;
